import type { Dossier, SeoScan, GscSnapshotData, SeoFinding } from '@/api/client'

// Only the 3 scan types this prompt actually renders — kept local (not
// imported from DossierDetail.tsx) so this module has no dependency on a
// page component; DossierDetail.tsx has its own broader copy for UI labels.
const SCAN_TYPE_LABELS: Record<string, string> = {
  onpage: 'On-page',
  tech_audit: 'Technisch',
  site_crawl: 'Site crawl',
}

/**
 * Build the Claude Code prompt for a dossier's SEO findings.
 *
 * S24 Task 5 (hardening, always present): a root-cause-first instruction
 * (don't fix an identical problem N times on N pages) and a guardrail
 * against destructive 404/de-index actions without explicit confirmation.
 *
 * S24 Task 1 integration (conditional): when the latest site_crawl carries
 * a rendering_regression finding, the prompt opens with it — findingsByType
 * already reflects the backend's suppression of the derived per-page noise,
 * so this just makes sure the one finding that matters isn't buried under
 * the (much shorter, now) rest of the report.
 */
export function generateSeoPrompt(
  dossier: Dossier,
  scans: SeoScan[],
  latestSnapshot: GscSnapshotData | undefined,
  findingsByType: Record<string, SeoFinding[]>,
  // Same value as the "Gemiddelde SEO-score" KPI card — passed in rather
  // than recomputed here so the two can never show different numbers again
  // (this used to omit content_audit and diverge from the card, e.g. "95"
  // here vs. "715" on the card).
  avg: number | null,
): string {
  const lines: string[] = []

  // ── WAF/blokkade detectie: waarschuw als veel bevindingen '202' als waarde hebben ──
  const allFindingsList = Object.values(findingsByType).flat()
  const blockedCount = allFindingsList.filter(f => f.value === '202' || f.check === 'page_blocked_by_waf').length
  const likelyBlocked = blockedCount >= 3 || allFindingsList.some(f => f.check === 'page_blocked_by_waf')

  if (likelyBlocked) {
    lines.push(`⚠️ OPGELET: SCANNER GEBLOKKEERD — RAPPORT ONBETROUWBAAR`)
    lines.push(``)
    lines.push(`De scanner kon ${dossier.url} niet bereiken (${blockedCount} bevindingen met waarde "202" of WAF-melding).`)
    lines.push(`Dit wijst op een WAF/bot-bescherming (Cloudflare, Wordfence, Sucuri, ...) die de scanner blokkeert.`)
    lines.push(``)
    lines.push(`→ FIX NIETS op basis van dit rapport. De onderstaande bevindingen zijn waarschijnlijk vals.`)
    lines.push(`→ Whitelist 'TechvibeSEO/1.0 (+https://techvibe.be/scanner)' in je beveiligingsplugin en scan opnieuw.`)
    lines.push(``)
    lines.push(`---`)
    lines.push(``)
    return lines.join('\n')
  }

  // ── S24 Task 5: rendering-regression gate — open with the P0, not buried ──
  // findingsByType.site_crawl already reflects the backend's Task 1 suppression
  // (the 45 derived findings on affected URLs are gone server-side); this just
  // makes sure the ONE finding that matters isn't scrolled past like any other.
  const regressionFinding = (findingsByType.site_crawl ?? []).find(
    f => f.check === 'rendering_regression'
  )
  if (regressionFinding) {
    lines.push(`🔴 RENDERING-REGRESSIE ACTIEF — LEES DIT EERST`)
    lines.push('')
    lines.push(regressionFinding.description || regressionFinding.name)
    lines.push('')
    lines.push(
      `Overige content-bevindingen (H1, soft-404, dunne inhoud, sectie-openers, interne links) ` +
      `op de getroffen pagina's zijn onderdrukt tot er een gezonde scan is — ze zijn allemaal ` +
      `hetzelfde symptoom van deze ene oorzaak, geen aparte problemen.`
    )
    if (regressionFinding.recommendation) lines.push(`→ ${regressionFinding.recommendation}`)
    lines.push('')
    lines.push('---')
    lines.push('')
  }

  // ── Header ──
  lines.push(`Optimaliseer de SEO van ${dossier.name} (${dossier.url}).`)
  if (dossier.client_name) lines.push(`Klant: ${dossier.client_name}`)
  if (dossier.baseline_date) lines.push(`Samenwerking gestart: ${new Date(dossier.baseline_date).toLocaleDateString('nl-BE')}`)
  if (avg !== null) lines.push(`Gemiddelde SEO-score: ${avg}/100`)
  lines.push('')

  if (dossier.last_gsc_clicks !== null && dossier.last_gsc_clicks !== undefined) {
    lines.push(`## GSC Prestaties (rolling 28 dagen)`)
    const pos = dossier.last_gsc_position !== null && dossier.last_gsc_position !== undefined
      ? ` · Gem. positie: #${dossier.last_gsc_position.toFixed(1)}` : ''
    lines.push(`Clicks: ${dossier.last_gsc_clicks.toLocaleString('nl')} · Impressies: ${(dossier.last_gsc_impressions ?? 0).toLocaleString('nl')}${pos}`)
    lines.push('')
  }

  lines.push(
    `Fix alle problemen hieronder in volgorde van prioriteit (kritiek eerst). ` +
    `Zoek zelf de relevante bestanden in de codebase. ` +
    `Verifieer na elke aanpassing dat de pagina correct rendert en geen andere pagina's breekt.`
  )
  lines.push(
    `Als meerdere pagina's een identiek probleem tonen: zoek eerst één gemeenschappelijke ` +
    `oorzaak (gedeelde include/partial/template) vóór je per pagina fixt.`
  )
  lines.push(
    `Zet nooit een 404/410-status op en de-indexeer nooit een URL die in de sitemap staat, ` +
    `zonder expliciete bevestiging van de beheerder.`
  )
  lines.push('')

  // ── Findings per scan type ──
  // Format finding value with unit context so pixel-width checks aren't
  // misread as character counts (e.g. "1058 px" instead of bare "1058").
  const _PX_CHECKS = new Set(['meta_description_too_long', 'title_too_long'])
  const _fmtValue = (f: SeoFinding) =>
    _PX_CHECKS.has(f.check) ? `${f.value} px` : f.value

  const SEVERITY_ORDER = ['critical', 'warning', 'notice']
  let taskNum = 1

  for (const type of ['onpage', 'tech_audit', 'site_crawl']) {
    const scan = scans.find(s => s.scan_type === type && s.status === 'completed')
    const findings = findingsByType[type] ?? []
    if (!scan) continue

    const actionable = findings.filter(f => f.severity === 'critical' || f.severity === 'warning')
    if (actionable.length === 0) {
      lines.push(`## ✅ ${SCAN_TYPE_LABELS[type]} — score ${scan.score ?? '?'}/100 (geen problemen)`)
      lines.push('')
      continue
    }

    lines.push(`## ${SCAN_TYPE_LABELS[type]} — score ${scan.score ?? '?'}/100`)
    lines.push('')

    // Group by severity
    for (const sev of SEVERITY_ORDER) {
      const sevFindings = actionable.filter(f => f.severity === sev)
      if (!sevFindings.length) continue
      const emoji = sev === 'critical' ? '🔴' : sev === 'warning' ? '🟠' : '🔵'
      lines.push(`### ${emoji} ${sev === 'critical' ? 'Kritieke problemen' : sev === 'warning' ? 'Waarschuwingen' : 'Verbeterpunten'} (${sevFindings.length})`)
      lines.push('')

      // For site_crawl: group by URL
      if (type === 'site_crawl') {
        const byUrl = new Map<string, SeoFinding[]>()
        for (const f of sevFindings) {
          const key = f.url || dossier.url
          if (!byUrl.has(key)) byUrl.set(key, [])
          byUrl.get(key)!.push(f)
        }
        byUrl.forEach((pageFindings, url) => {
          if (url !== dossier.url && url !== dossier.url.replace(/\/$/, '')) {
            lines.push(`#### ${url}`)
          }
          for (const f of pageFindings) {
            lines.push(`${taskNum++}. **${f.name}**`)
            if (f.value) lines.push(`   Huidige waarde: \`${_fmtValue(f)}\``)
            lines.push(`   → ${f.recommendation}`)
          }
          lines.push('')
        })
      } else {
        for (const f of sevFindings) {
          lines.push(`${taskNum++}. **${f.name}**`)
          if (f.url && f.url !== dossier.url) lines.push(`   Pagina: ${f.url}`)
          if (f.value) lines.push(`   Huidige waarde: \`${_fmtValue(f)}\``)
          lines.push(`   → ${f.recommendation}`)
        }
        lines.push('')
      }
    }
  }

  // ── GSC quick wins ──
  if (latestSnapshot?.top_queries) {
    const quickWins = (latestSnapshot.top_queries as { query: string; position: number; impressions: number }[])
      .filter(q => q.position >= 11 && q.position <= 20 && q.impressions >= 50)
      .slice(0, 3)
    if (quickWins.length > 0) {
      lines.push(`## 🎯 GSC Quick wins — Bijna pagina 1`)
      lines.push('')
      for (const q of quickWins) {
        lines.push(`${taskNum++}. **"${q.query}"** staat op positie #${Math.round(q.position)} met ${q.impressions} impressies`)
        lines.push(`   → Versterk de pagina die rankt voor dit zoekwoord: betere content, interne links, schema markup.`)
      }
      lines.push('')
    }
  }

  lines.push('---')
  lines.push(`Na alle SEO-fixes: klik "Volledig scannen (3×)" om een nieuwe scan te starten.`)

  return lines.join('\n')
}
